/**
 * DISCOVERY CONFIRMED:
 * Ranking 1 = LOW (least visibility, appears last)
 * Ranking 5 = TOP (maximum visibility, appears first)
 * Sort order: ORDER BY ranking DESC
 * Tested on: 2026-06-19
 */

export const MIN_RANKING = 1;
export const MAX_RANKING = 5;

/** Shared with CreateProperty / EditProperty form sections */
export const PAGE_BLUE = {
  primary: "#6EA5FF",
  primaryHover: "#5c94ed",
  link: "#3b82f6",
  deep: "#3d6fa8",
} as const;

/** Tailwind tokens aligned to property form twStyles */
export const STAR_RANKING_SURFACE_CLASS =
  "rounded-md border border-slate-200 bg-white p-5 sm:p-8";

export const FILLED_STAR_CLASS = "text-[#6EA5FF]";
export const EMPTY_STAR_COLOR = "text-slate-300";
export const ACTIVE_STAR_LABEL_CLASS = "text-[#3d6fa8]";
export const STAR_GLOW_CLASS = "bg-[#6EA5FF]/20";

export interface RankConfigItem {
  label: string;
  shortLabel: string;
  title: string;
  description: string;
  badge: string;
  tooltip: string;
}

export const RANK_CONFIG: Record<number, RankConfigItem> = {
  1: {
    label: "Padrão",
    shortLabel: "P",
    title: "Visibilidade Padrão",
    description: "Posicionamento básico nas listagens",
    badge: "Padrão",
    tooltip: "Aparece após todos os destaques",
  },
  2: {
    label: "Destaque Leve",
    shortLabel: "DL",
    title: "Destaque Inicial",
    description: "Visibilidade acima da média",
    badge: "Leve",
    tooltip: "Posicionamento intermediário",
  },
  3: {
    label: "Destaque Médio",
    shortLabel: "DM",
    title: "Boa Visibilidade",
    description: "Posição de destaque nas buscas",
    badge: "Médio",
    tooltip: "Excelente posicionamento",
  },
  4: {
    label: "Destaque Alto",
    shortLabel: "DA",
    title: "Alta Visibilidade",
    description: "Prioridade máxima nas listagens",
    badge: "Alto",
    tooltip: "Aparece entre os primeiros",
  },
  5: {
    label: "Destaque Máximo",
    shortLabel: "DMX",
    title: "Visibilidade Máxima",
    description: "O mais alto nível de destaque disponível",
    badge: "Máximo",
    tooltip: "Posição de topo nas buscas",
  },
};

export function clampRanking(value: number): number {
  return Math.min(MAX_RANKING, Math.max(MIN_RANKING, value));
}

export function getRankConfig(rank: number): RankConfigItem | undefined {
  return RANK_CONFIG[rank];
}

export function getRankLabel(rank: number): string {
  return RANK_CONFIG[rank]?.label ?? "";
}

export function getRankShortLabel(rank: number): string {
  return RANK_CONFIG[rank]?.shortLabel ?? "";
}

export function getRankTitle(rank: number): string {
  return RANK_CONFIG[rank]?.title ?? "";
}

export function getRankDescription(rank: number): string {
  return RANK_CONFIG[rank]?.description ?? "";
}

export function getRankBadge(rank: number): string {
  return RANK_CONFIG[rank]?.badge ?? "";
}

export function getRankTooltip(rank: number): string {
  return RANK_CONFIG[rank]?.tooltip ?? "";
}

export function getStarColor(star: number, currentRank: number): string {
  return star <= currentRank ? FILLED_STAR_CLASS : EMPTY_STAR_COLOR;
}

export function getGlowColor(star: number, currentRank: number): string {
  return star <= currentRank ? STAR_GLOW_CLASS : "";
}

export function getTextColor(star: number, currentRank: number): string {
  return star <= currentRank ? ACTIVE_STAR_LABEL_CLASS : "text-slate-400";
}

export function getProgressColor(ranking: number): string {
  return ranking > 0 ? "bg-[#6EA5FF]" : "bg-slate-200";
}

export function getPreviewSurfaceClass(hasSelection: boolean): string {
  return hasSelection
    ? "rounded-md border border-slate-200 bg-slate-50"
    : "rounded-md border border-slate-200 bg-slate-50";
}

export function getRankIndicatorTextColor(): string {
  return "text-[#3d6fa8]";
}

export function isValidRanking(value: number): boolean {
  return value >= MIN_RANKING && value <= MAX_RANKING;
}
